import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { systemApi } from '@/lib/api'
import { Settings as SettingsIcon, Loader2, Save } from 'lucide-react'

export function PreferencePage() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preferences, setPreferences] = useState({
    time_range: 7,
    precise_filter: false,
    emotion: 'all',
    similar: true,
    sort: 0,
    match_mode: 0,
    data_source: 'all',
  })

  useEffect(() => {
    // Load preferences
    setLoading(true)
    systemApi.getOpinionCondition('').then((res: any) => {
      if (res?.data) {
        setPreferences(prev => ({ ...prev, ...res.data }))
      }
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await systemApi.updateOpinionCondition(preferences)
    } catch (err) {
      console.error('Failed to save preferences:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <h2 className="text-xl font-semibold flex items-center gap-2">
        <SettingsIcon className="h-5 w-5" />
        {t('settings.preference.title')}
      </h2>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('settings.preference.timeRange')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={String(preferences.time_range)} onValueChange={(v) => setPreferences({ ...preferences, time_range: Number(v) })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">{t('monitor.hours24')}</SelectItem>
              <SelectItem value="2">{t('monitor.today')}</SelectItem>
              <SelectItem value="4">{t('monitor.days3')}</SelectItem>
              <SelectItem value="5">{t('monitor.days7')}</SelectItem>
              <SelectItem value="6">{t('monitor.days15')}</SelectItem>
              <SelectItem value="7">{t('monitor.days30')}</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('settings.preference.emotion')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={preferences.emotion} onValueChange={(v) => setPreferences({ ...preferences, emotion: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="1">{t('monitor.positive')}</SelectItem>
              <SelectItem value="0">{t('monitor.neutral')}</SelectItem>
              <SelectItem value="-1">{t('monitor.negative')}</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('settings.preference.sort')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={String(preferences.sort)} onValueChange={(v) => setPreferences({ ...preferences, sort: Number(v) })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">{t('monitor.timeDesc')}</SelectItem>
              <SelectItem value="1">{t('monitor.timeAsc')}</SelectItem>
              <SelectItem value="2">{t('monitor.similarDesc')}</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('settings.preference.matchMode')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={String(preferences.match_mode)} onValueChange={(v) => setPreferences({ ...preferences, match_mode: Number(v) })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">{t('monitor.fullText')}</SelectItem>
              <SelectItem value="1">{t('monitor.titleOnly')}</SelectItem>
              <SelectItem value="2">{t('monitor.contentOnly')}</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center justify-between">
            <Label>{t('settings.preference.preciseFilter')}</Label>
            <Switch
              checked={preferences.precise_filter}
              onCheckedChange={(v) => setPreferences({ ...preferences, precise_filter: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>{t('settings.preference.similar')}</Label>
            <Switch
              checked={preferences.similar}
              onCheckedChange={(v) => setPreferences({ ...preferences, similar: v })}
            />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        {t('common.save')}
      </Button>
    </div>
  )
}
